import React from 'react';
import { TestimonialCard } from './TestimonialCard';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ClipboardListIcon, MicroscopeIcon, FileTextIcon, MessageCircleIcon, CreditCardIcon } from 'lucide-react';
export function SolutionSection() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  const steps = [{
    icon: <ClipboardListIcon className="w-8 h-8 text-teal-600" data-id="element-345" />,
    title: 'STEP 1: Pre-Session Biological Dossier Review',
    desc: "Before you ever get on the call you'll submit the most important details. Labs, imaging, medications, previous diagnoses, symptoms, everything. Either Brenden or Dr. Ali will review your case. Looking for patterns. Spotting what's been missed."
  }, {
    icon: <MicroscopeIcon className="w-8 h-8 text-teal-600" data-id="element-346" />,
    title: 'STEP 2: 2–3 Hour Precision Cell Command Session',
    desc: "You'll spend 2–3 HOURS with either Brenden or Dr. Ali... Going through your case line by line. They'll dive into what's REALLY going on. Not just surface symptoms... But the root biological causes driving everything. They'll be getting a deep understanding of what's happening with you at the CELLULAR level."
  }, {
    icon: <FileTextIcon className="w-8 h-8 text-teal-600" data-id="element-347" />,
    title: 'STEP 3: Your Precision Cell Blueprint',
    desc: "After your session (and sometimes even right at the end of your session) you'll get a written, holistic program EXPERTLY crafted by Dr. Ali or Brenden. It's the exact roadmap showing you: What to do, When to do it, In what order, What tests to further run, Which compounds to use, Dosing and timing guidelines etc."
  }, {
    icon: <MessageCircleIcon className="w-8 h-8 text-teal-600" data-id="element-348" />,
    title: 'STEP 4: 30 Day Implementation Support',
    desc: "You're not being abandoned after the session. For 30 days after your call you'll be able to receive basic support from your specialist, so if questions come up about your program, or you need help fine-tuning the protocol... You're covered."
  }, {
    icon: <CreditCardIcon className="w-8 h-8 text-teal-600" data-id="element-349" />,
    title: 'STEP 5: Full Transformation Program Credit',
    desc: 'If after going through this process you decide you want full hands-on support from our team, and you enroll in one of our Precision Cell Transformation Programs within 30 days... You get the full credit applied to your program. Which means this Advanced session basically becomes free.'
  }];
  return <div ref={ref} className="grain-section w-full px-6 py-32 md:py-40 bg-white relative" data-id="element-350">
      {/* Chapter indicator */}
      <motion.div initial={{
      opacity: 0,
      x: -20
    }} animate={inView ? {
      opacity: 1,
      x: 0
    } : {}} transition={{
      duration: 0.8
    }} className="max-w-5xl mx-auto mb-24" data-id="element-351">
        <div className="flex flex-col md:flex-row md:items-center gap-8 mb-8" data-id="element-352">
          <div className="text-8xl md:text-9xl font-black text-teal-50 font-sans tracking-tighter" data-id="element-353">
            02
          </div>
          <div className="h-px w-24 bg-teal-100 hidden md:block" data-id="element-354"></div>
          <div data-id="element-355">
            <p className="text-xs text-teal-600 font-bold tracking-widest uppercase mb-3" data-id="element-356">
              Chapter Two
            </p>
            <h2 className="text-4xl md:text-6xl font-bold text-ink tracking-tight" data-id="element-357">
              Why Most "Expert Consults" <br data-id="element-358" />
              <span className="text-teal-600" data-id="element-359">Are Basically Useless</span>
            </h2>
          </div>
        </div>
      </motion.div>

      <div className="max-w-5xl mx-auto space-y-20" data-id="element-360">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        delay: 0.3,
        duration: 0.8
      }} className="space-y-8 text-xl text-ink-light leading-relaxed font-light" data-id="element-361">
          <p data-id="element-362">
            Let's be honest about what usually happens when you book a
            consultation. You pay your money... You show up to the call... And
            the "expert" asks you a bunch of surface-level questions they
            could've asked beforehand.
          </p>
          <p data-id="element-363">
            They nod along while you explain your situation... (Which they're
            hearing for the FIRST time...) Then they give you some generic
            advice... Maybe recommend a few supplements... And send you on your
            way.
          </p>

          <div className="bg-page-bg p-10 rounded-3xl border border-black/5 text-center" data-id="element-364">
            <p className="text-2xl font-bold text-ink" data-id="element-365">
              That's not mastery. That's just expensive guessing. And you don't
              need more guesses. You need ANSWERS.
            </p>
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        delay: 0.5,
        duration: 0.8
      }} className="space-y-12 pt-12" data-id="element-366">
          <h3 className="text-3xl md:text-5xl font-bold text-center text-ink tracking-tight" data-id="element-367">
            How Meeting With the Master: <br data-id="element-368" />
            <span className="text-teal-600" data-id="element-369">ADVANCED Actually Works</span>
          </h3>

          <div className="grid gap-8" data-id="element-370">
            {steps.map((step, index) => <div key={index} className="bg-white border border-black/5 rounded-3xl p-8 md:p-10 shadow-sm hover:shadow-lg hover:border-teal-200 transition-all duration-300" data-id="element-371">
                <div className="flex flex-col md:flex-row gap-6 md:gap-8" data-id="element-372">
                  <div className="bg-teal-50 p-4 rounded-2xl self-start" data-id="element-373">
                    {step.icon}
                  </div>
                  <div className="space-y-4" data-id="element-374">
                    <h4 className="text-xl md:text-2xl font-bold text-ink" data-id="element-375">
                      {step.title}
                    </h4>
                    <p className="text-lg text-ink-light font-light leading-relaxed" data-id="element-376">
                      {step.desc}
                    </p>
                  </div>
                </div>
              </div>)}
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        scale: 0.98
      }} animate={inView ? {
        opacity: 1,
        scale: 1
      } : {}} transition={{
        delay: 0.7,
        duration: 0.8
      }} data-id="element-377">
          <TestimonialCard text="Thank you Ali... You and Brenden have been a light for both of us. You have done what our entire team of medical professionals were not able to do. I am looking forward to doing a program with you myself after my husband's recovery." highlight={true} profileImage="https://uploadthingy.s3.us-west-1.amazonaws.com/rdsSxPNyzo6kTft2Q6Yhgt/Screenshot_2025-11-10_200825.png" name="Badar A., Peptide Science Institute Transformation Services Client" data-id="element-378" />
        </motion.div>
      </div>
    </div>;
}